// ============================================================================
// OMNIPASS FRONTEND - API SERVICES
// Complete API service functions organized by business logic
// ============================================================================

import api, { getAccessToken, setAccessToken } from './api';
import { walletMembersAPI } from './walletsAPI';
import type {
  AuthRequest,
  RegisterRequest,
  AuthResponse,
  UserProfile,
  RefreshTokenRequest,
  TwoFactorSetupRequest,
  TwoFactorSetupResponse,
  TwoFactorEnableRequest,
  TwoFactorEnableResponse,
  TwoFactorLoginRequest,
  PassTemplate,
  CreateTemplateRequest,
  PassInstance,
  MembershipTierRequest,
  MembershipPassRequest,
  MembershipPassResponse,
  MemberPointsUpdate,
  MemberPointsResponse,
  CouponRequest,
  EventTicketRequest,
  AnalyticsData,
  TemplateAnalytics,
  UserListItem,
  CompanyInfo,
  CompanyListItem,
  ImageUploadResponse,
  APIError,
  DashboardStats,
  CompanyProgram,
  CompanyTier,
  CompanyMember,
  Campaign,
  Offer,
  CouponPass,
  PagedApiResponse,
} from '../types/api';

// ============================================================================
// AUTHENTICATION API
// ============================================================================

export const authAPI = {
  // Login sa email/password
  login: async (credentials: AuthRequest): Promise<AuthResponse> => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },

  // Login sa 2FA
  loginWith2FA: async (credentials: TwoFactorLoginRequest): Promise<AuthResponse> => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },

  // Register novi user
  register: async (userData: RegisterRequest): Promise<AuthResponse> => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  // Refresh access token
  refresh: async (refreshData: RefreshTokenRequest): Promise<AuthResponse> => {
    const response = await api.post('/auth/refresh', refreshData);
    return response.data;
  },

  // Logout
  logout: async (): Promise<void> => {
    // Refresh token is in an HttpOnly cookie — do not read from sessionStorage.
    await api.post('/auth/logout');
  },

  // Complete login with 2FA verification
  verifyTwoFactorLogin: async (data: TwoFactorLoginRequest): Promise<AuthResponse> => {
    // Filter out undefined values to avoid backend confusion
    const cleanData: any = {
      user_id: data.user_id,
    };
    if (data.totp_code) {
      cleanData.totp_code = data.totp_code;
    }
    if (data.backup_code) {
      cleanData.backup_code = data.backup_code;
    }

    const response = await api.post<AuthResponse>('/auth/login/verify-2fa', cleanData);
    return response.data;
  },

  // Get user status/profile
  getUserStatus: async (): Promise<UserProfile> => {
    const response = await api.get('/users/me');
    return response.data;
  },
};

// ============================================================================
// TWO-FACTOR AUTHENTICATION API
// ============================================================================

export const twoFactorAPI = {
  // Setup 2FA - generiraj secret i QR code
  setup: async (data: TwoFactorSetupRequest): Promise<TwoFactorSetupResponse> => {
    const response = await api.post('/two-factor/setup', data);
    return response.data;
  },

  // Enable 2FA - finalni korak aktivacije
  enable: async (data: TwoFactorEnableRequest): Promise<TwoFactorEnableResponse> => {
    const response = await api.post('/two-factor/enable', data);
    return response.data;
  },

  // Disable 2FA
  disable: async (password: string): Promise<{ success: boolean }> => {
    const response = await api.post('/two-factor/disable', { password });
    return response.data;
  },

  // Generate new backup codes
  generateBackupCodes: async (): Promise<{ backup_codes: string[] }> => {
    const response = await api.post('/two-factor/regenerate-backup');
    return response.data;
  },
};

// ============================================================================
// TEMPLATES API
// ============================================================================

export const templatesAPI = {
  // Get all templates za current company
  getAll: async (): Promise<PassTemplate[]> => {
    const response = await api.get('/templates');
    return response.data;
  },

  // Get template by ID
  getById: async (id: string): Promise<PassTemplate> => {
    const response = await api.get(`/templates/${id}`);
    return response.data;
  },

  // Create new template
  create: async (templateData: CreateTemplateRequest): Promise<PassTemplate> => {
    const response = await api.post('/templates', templateData);
    return response.data;
  },

  // Update template
  update: async (id: string, templateData: Partial<CreateTemplateRequest>): Promise<PassTemplate> => {
    const response = await api.put(`/templates/${id}`, templateData);
    return response.data;
  },

  // Delete template
  delete: async (id: string): Promise<void> => {
    await api.delete(`/templates/${id}`);
  },
};

// ============================================================================
// MEMBERSHIP API
// ============================================================================

export const membershipAPI = {
  // Create membership tier template
  createCard: async (data: MembershipTierRequest): Promise<PassInstance> => {
    const response = await api.post('/membership/cards', data);
    return response.data;
  },

  // Get all membership tier templates
  getCards: async (): Promise<PassInstance[]> => {
    const response = await api.get('/membership/cards');
    return response.data;
  },

  // Update membership tier template
  updateCard: async (id: string, data: Partial<MembershipTierRequest>): Promise<PassInstance> => {
    const response = await api.put(`/membership/cards/${id}`, data);
    return response.data;
  },

  // Delete membership tier template
  deleteCard: async (id: string): Promise<void> => {
    await api.delete(`/membership/cards/${id}`);
  },
};

// ============================================================================
// MEMBERSHIP PASSES/MEMBERS API (Individual Members)
// ============================================================================

export const memberPassAPI = {
  // Create new member from template
  createMember: async (templateId: string, memberData: MembershipPassRequest): Promise<MembershipPassResponse> => {
    const response = await api.post(`/membership/templates/${templateId}/passes`, memberData);
    return response.data;
  },

  // Get all members for a specific template
  getMembersByTemplate: async (templateId: string): Promise<MembershipPassResponse[]> => {
    const response = await api.get(`/membership/templates/${templateId}/passes`);
    return response.data;
  },

  // Get all members across all templates (for company)
  getAllMembers: async (): Promise<MembershipPassResponse[]> => {
    const response = await api.get('/membership/passes');
    return response.data;
  },

  // Get specific member by pass ID
  getMember: async (passId: string): Promise<MembershipPassResponse> => {
    const response = await api.get(`/membership/passes/${passId}`);
    return response.data;
  },

  // Update member information
  updateMember: async (passId: string, memberData: Partial<MembershipPassRequest>): Promise<MembershipPassResponse> => {
    const response = await api.put(`/membership/passes/${passId}`, memberData);
    return response.data;
  },

  // Delete member
  deleteMember: async (passId: string): Promise<void> => {
    await api.delete(`/membership/passes/${passId}`);
  },

  // Update member points
  updatePoints: async (passId: string, pointsData: MemberPointsUpdate): Promise<MemberPointsResponse> => {
    const response = await api.post(`/membership/passes/${passId}/points`, pointsData);
    return response.data;
  },

  // Upgrade member to different tier
  upgradeTier: async (passId: string, newTierId: string): Promise<MembershipPassResponse> => {
    const response = await api.post(`/membership/passes/${passId}/upgrade`, { new_tier_id: newTierId });
    return response.data;
  },

  // Resend pass links to member
  resendPass: async (passId: string): Promise<{ success: boolean }> => {
    const response = await api.post(`/membership/passes/${passId}/resend`);
    return response.data;
  },
};

// ============================================================================
// COUPONS API
// ============================================================================

export const couponsAPI = {
  // Create coupon
  createCoupon: async (data: CouponRequest): Promise<PassInstance> => {
    const response = await api.post('/coupons', data);
    return response.data;
  },

  // Get all coupons
  getCoupons: async (): Promise<PassInstance[]> => {
    const response = await api.get('/coupons');
    return response.data;
  },

  // Update coupon
  updateCoupon: async (id: string, data: Partial<CouponRequest>): Promise<PassInstance> => {
    const response = await api.put(`/coupons/${id}`, data);
    return response.data;
  },

  // Delete coupon
  deleteCoupon: async (id: string): Promise<void> => {
    await api.delete(`/coupons/${id}`);
  },
};

// ============================================================================
// EVENTS API
// ============================================================================

export const eventsAPI = {
  // Create event ticket
  createTicket: async (data: EventTicketRequest): Promise<PassInstance> => {
    const response = await api.post('/events/tickets', data);
    return response.data;
  },

  // Get all event tickets
  getTickets: async (): Promise<PassInstance[]> => {
    const response = await api.get('/events/tickets');
    return response.data;
  },

  // Update event ticket
  updateTicket: async (id: string, data: Partial<EventTicketRequest>): Promise<PassInstance> => {
    const response = await api.put(`/events/tickets/${id}`, data);
    return response.data;
  },

  // Delete event ticket
  deleteTicket: async (id: string): Promise<void> => {
    await api.delete(`/events/tickets/${id}`);
  },
};

// ============================================================================
// ANALYTICS API
// ============================================================================

export const analyticsAPI = {
  // Get analytics data
  getAnalytics: async (timeRange?: string): Promise<AnalyticsData> => {
    const params = timeRange ? { time_range: timeRange } : {};
    const response = await api.get('/analytics', { params });
    return response.data;
  },

  // Get pass analytics by template
  getTemplateAnalytics: async (templateId: string): Promise<TemplateAnalytics> => {
    const response = await api.get(`/analytics/templates/${templateId}`);
    return response.data;
  },

  // Get dashboard stats
  getDashboardStats: async (): Promise<DashboardStats> => {
    const response = await api.get('/analytics/dashboard');
    return response.data;
  },

  // Export analytics data
  exportAnalytics: async (format: 'csv' | 'excel' = 'csv'): Promise<Blob> => {
    const response = await api.get(`/analytics/export`, {
      params: { format },
      responseType: 'blob'
    });
    return response.data;
  },
};

// ============================================================================
// USERS API (Admin only)
// ============================================================================

export const usersAPI = {
  // Get all users (Admin only)
  getAll: async (): Promise<UserListItem[]> => {
    const response = await api.get('/users');
    return response.data;
  },

  // Get user by ID
  getById: async (id: string): Promise<UserListItem> => {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },

  // Update user (Admin only)
  update: async (id: string, userData: Partial<UserListItem>): Promise<UserListItem> => {
    const response = await api.patch(`/users/${id}`, userData);
    return response.data;
  },

  // Delete user (Admin only)
  delete: async (id: string): Promise<void> => {
    await api.delete(`/users/${id}`);
  },
};

// ============================================================================
// COMPANIES API
// ============================================================================

export const companiesAPI = {
  // Get company info
  getInfo: async (): Promise<CompanyInfo> => {
    const response = await api.get('/companies/info');
    return response.data;
  },

  // Update company settings
  updateSettings: async (companyData: Partial<CompanyInfo>): Promise<CompanyInfo> => {
    const response = await api.patch('/companies/settings', companyData);
    return response.data;
  },
};

// ============================================================================
// IMAGES API
// ============================================================================

export const imagesAPI = {
  // Upload image
  uploadImage: async (file: File, type: 'logo' | 'background' | 'icon'): Promise<ImageUploadResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);

    const response = await api.post('/images/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Get image by ID
  getImage: async (id: string): Promise<string> => {
    const response = await api.get(`/images/${id}`);
    return response.data.url;
  },
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

export const healthCheck = async (): Promise<boolean> => {
  try {
    const response = await api.get('/health'.replace('/api/v1', ''));
    return response.status === 200;
  } catch (error) {
    return false;
  }
};

// Check if user is authenticated (uses in-memory token — not sessionStorage)
export const isAuthenticated = (): boolean => {
  return !!getAccessToken();
};

// Get stored user profile
export const getStoredUserProfile = (): UserProfile | null => {
  return null;
};

// Store user profile — no-op in modern auth flow
export const storeUserProfile = (_profile: UserProfile): void => {};

// Clear all auth data
export const clearAuthData = (): void => {
  setAccessToken(null);
};

// ============================================================================
// ERROR HANDLING UTILITIES
// ============================================================================

export const handleAPIError = (error: any): string => {
  if (error.response?.data) {
    const data = error.response.data as APIError;
    return data.detail || 'Došlo je do greške';
  }

  if (error.message) {
    return error.message;
  }

  return 'Neočekivana greška';
};

// ============================================================================
// MULTI-TENANT MEMBERSHIP API (New B2B Architecture)
// ============================================================================

export const multiTenantMembershipAPI = {
  // Programs (highest level in multi-tenant hierarchy)
  getPrograms: async (): Promise<{ programs: CompanyProgram[]; meta?: PagedApiResponse<CompanyProgram>['meta'] }> => {
    const response = await api.get('/membership/programs');
    const body = response.data;
    // Canonical envelope: { data: [...], meta: {...} }; legacy: { programs: [...] }
    return { programs: body.data ?? body.programs ?? body, meta: body.meta };
  },

  getProgram: async (programId: string): Promise<CompanyProgram> => {
    const response = await api.get(`/membership/programs/${programId}`);
    return response.data;
  },

  // Members by program
  getProgramMembers: async (programId: string, page = 1, per_page = 100): Promise<{ members: CompanyMember[]; meta?: PagedApiResponse<CompanyMember>['meta'] }> => {
    const response = await api.get(`/membership/programs/${programId}/members`, { params: { page, per_page } });
    const body = response.data;
    return { members: body.data ?? body, meta: body.meta };
  },

  // All members across all programs (for company dashboard)
  getAllMembers: async (params?: { programId?: string; page?: number; per_page?: number }): Promise<{ members: CompanyMember[]; meta?: PagedApiResponse<CompanyMember>['meta'] }> => {
    const searchParams = new URLSearchParams();
    if (params?.programId && params.programId !== 'all') {
      searchParams.append('programId', params.programId);
    }
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.per_page) searchParams.append('per_page', params.per_page.toString());
    const url = searchParams.toString() ? `/membership/members?${searchParams.toString()}` : '/membership/members';
    const response = await api.get(url);
    const body = response.data;
    return { members: body.data ?? body.members ?? body, meta: body.meta ?? body.pagination };
  },

  // Member search and filtering
  searchMembers: async (params: {
    search?: string;
    program_id?: string;
    tier_id?: string;
    is_active?: boolean;
  }): Promise<CompanyMember[]> => {
    const searchParams = new URLSearchParams();
    if (params.search) searchParams.append('search', params.search);
    if (params.program_id) searchParams.append('program_id', params.program_id);
    if (params.tier_id) searchParams.append('tier_id', params.tier_id);
    if (params.is_active !== undefined) searchParams.append('is_active', params.is_active.toString());

    const response = await api.get(`/membership/members?${searchParams.toString()}`);
    return response.data;
  },

  // Tiers management
  getTiers: async (): Promise<CompanyTier[]> => {
    const response = await api.get('/membership/tiers');
    return response.data;
  },

  getProgramTiers: async (programId: string): Promise<CompanyTier[]> => {
    const response = await api.get(`/membership/programs/${programId}/tiers`);
    return response.data;
  },

  // Company tiers (real PassKit integration) - automatically loads all tiers for the user's company
  getTemplateTiers: async (templateId?: string): Promise<any> => {
    // New endpoint loads all tiers for the company automatically
    const response = await api.get(`/membership/tiers`);
    return response.data;
  },

  // Create new tier for program
  createProgramTier: async (programId: string, tierData: Partial<CompanyTier>): Promise<CompanyTier> => {
    const response = await api.post(`/membership/programs/${programId}/tiers`, tierData);
    return response.data;
  },

  // Update tier in program
  updateProgramTier: async (programId: string, tierId: string, tierData: Partial<CompanyTier>): Promise<CompanyTier> => {
    const response = await api.patch(`/membership/programs/${programId}/tiers/${tierId}`, tierData);
    return response.data;
  },

  // Delete tier from program
  deleteProgramTier: async (programId: string, tierId: string): Promise<any> => {
    const response = await api.delete(`/membership/programs/${programId}/tiers/${tierId}`);
    return response.data;
  },

  // Get tier colors directly by tier ID
  getTierColors: async (tierId: string, companyId?: string): Promise<any> => {
    const url = `/membership/tiers/${tierId}/colors`;
    const params = companyId ? { company_id: companyId } : {};
    const response = await api.get(url, { params });
    return response.data;
  },

  // Update tier colors directly by tier ID (with company isolation)
  updateTierColorsDirect: async (tierId: string, colors: Record<string, string>, companyId?: string): Promise<CompanyTier> => {
    const url = `/membership/tiers/${tierId}/colors`;
    const params = companyId ? { company_id: companyId } : {};
    const response = await api.patch(url, colors, { params });
    return response.data;
  },

  // Get all company programs (NEW: includes admin multi-tenant support)
  getCompanyPrograms: async (): Promise<PagedApiResponse<CompanyProgram>> => {
    const response = await api.get('/membership/programs');
    return response.data;
  },

  // Get companies for admin dropdown
  getCompanies: async (): Promise<CompanyListItem[]> => {
    const response = await api.get('/membership/companies');
    return response.data;
  },
};

// ============================================================================
// MULTI-TENANT COUPON API (New B2B Architecture)
// Hierarchical structure: Campaign → Offer → Coupon
// ============================================================================

export const multiTenantCouponAPI = {
  // ========================================
  // TEMPLATES (Coupon Pass Templates from PassKit)
  // ========================================

  getTemplates: async (campaignId?: string): Promise<any[]> => {
    const params = campaignId ? `?campaign_id=${campaignId}` : '';
    const response = await api.get(`/coupons/templates${params}`);
    return response.data;
  },

  // ========================================
  // CAMPAIGNS (Top Level - like Resources/Programs)
  // ========================================

  getCampaigns: async (): Promise<PagedApiResponse<Campaign>> => {
    const response = await api.get('/coupons/campaigns');
    return response.data;
  },

  getCampaign: async (campaignId: string): Promise<Campaign> => {
    const response = await api.get(`/coupons/campaigns/${campaignId}`);
    return response.data;
  },

  // ========================================
  // OFFERS (Mid Level - like Tiers)
  // ========================================

  getOffers: async (campaignId?: string, syncFromPasskit?: boolean): Promise<Offer[]> => {
    const params = new URLSearchParams();
    if (campaignId) params.append('campaign_id', campaignId);
    if (syncFromPasskit) params.append('sync_from_passkit', 'true');

    const url = params.toString()
      ? `/coupons/offers?${params.toString()}`
      : '/coupons/offers';
    const response = await api.get(url);
    return response.data;
  },

  getOffer: async (offerId: string, campaignId: string): Promise<Offer> => {
    const response = await api.get(`/coupons/offers/${offerId}?campaign_id=${campaignId}`);
    return response.data;
  },

  createOffer: async (campaignId: string, offerData: Partial<Offer>): Promise<Offer> => {
    const response = await api.post('/coupons/offers', {
      campaign_id: campaignId,
      ...offerData
    });
    return response.data;
  },

  updateOffer: async (offerId: string, campaignId: string, offerData: Partial<Offer>): Promise<Offer> => {
    const response = await api.patch(`/coupons/offers/${offerId}`, {
      campaign_id: campaignId,
      ...offerData
    });
    return response.data;
  },

  deleteOffer: async (offerId: string, campaignId: string): Promise<void> => {
    await api.delete(`/coupons/offers/${offerId}?campaign_id=${campaignId}`);
  },

  // ========================================
  // COUPONS (Individual Instances - like Members)
  // ========================================

  getCoupons: async (params?: {
    offer_id?: string;
    campaign_id?: string;
  }): Promise<CouponPass[]> => {
    const searchParams = new URLSearchParams();
    if (params?.offer_id) searchParams.append('offer_id', params.offer_id);
    if (params?.campaign_id) searchParams.append('campaign_id', params.campaign_id);

    const url = searchParams.toString()
      ? `/coupons/passes?${searchParams.toString()}`
      : '/coupons/passes';
    const response = await api.get(url);
    return response.data;
  },

  getCoupon: async (couponId: string): Promise<CouponPass> => {
    const response = await api.get(`/coupons/passes/${couponId}`);
    return response.data;
  },

  getCouponByCode: async (couponCode: string): Promise<any> => {
    const response = await api.get(`/coupons/passes/code/${couponCode}`);
    return response.data;
  },

  createCoupon: async (couponData: any): Promise<any> => {
    const response = await api.post('/coupons/passes', couponData);
    return response.data;
  },

  updateCoupon: async (couponId: string, couponData: any): Promise<any> => {
    const response = await api.patch(`/coupons/passes/${couponId}`, couponData);
    return response.data;
  },

  deleteCoupon: async (couponId: string): Promise<void> => {
    await api.delete(`/coupons/passes/${couponId}`);
  },

  // Alias for deleteCoupon (DELETE = void in PassKit)
  voidCoupon: async (couponId: string): Promise<void> => {
    await api.delete(`/coupons/passes/${couponId}`);
  },

  // ========================================
  // REDEMPTION OPERATIONS
  // ========================================

  redeemCoupon: async (couponId: string, redemptionData?: any): Promise<any> => {
    const response = await api.post(`/coupons/passes/${couponId}/redeem`, redemptionData || {});
    return response.data;
  },

  bulkRedeemCoupons: async (couponIds: string[], redemptionData?: any): Promise<any> => {
    const response = await api.post('/coupons/passes/bulk/redeem', {
      coupon_ids: couponIds,
      redemption_data: redemptionData
    });
    return response.data;
  },

  // ========================================
  // BULK OPERATIONS
  // ========================================

  bulkDeleteCoupons: async (couponIds: string[]): Promise<any> => {
    const response = await api.post('/coupons/passes/bulk/delete', {
      coupon_ids: couponIds
    });
    return response.data;
  },

  // ========================================
  // SEARCH & FILTERING
  // ========================================

  searchCoupons: async (params: {
    search?: string;
    campaign_id?: string;
    offer_id?: string;
    status?: string;
  }): Promise<any[]> => {
    const searchParams = new URLSearchParams();
    if (params.search) searchParams.append('search', params.search);
    if (params.campaign_id) searchParams.append('campaign_id', params.campaign_id);
    if (params.offer_id) searchParams.append('offer_id', params.offer_id);
    if (params.status) searchParams.append('status', params.status);

    const response = await api.get(`/coupons/passes?${searchParams.toString()}`);
    return response.data;
  },
};

// ============================================================================
// PERFORMANCE MONITORING API
// ============================================================================

export const performanceAPI = {
  // Get system health metrics
  getSystemHealth: async () => {
    const response = await api.get('/monitoring/performance/health');
    return response.data;
  },

  // Get performance summary
  getPerformanceSummary: async () => {
    const response = await api.get('/monitoring/performance/summary');
    return response.data;
  },

  // Get endpoint statistics
  getEndpointStats: async (sortBy: string = 'requests', limit: number = 15) => {
    const response = await api.get(`/monitoring/performance/endpoints?sort_by=${sortBy}&limit=${limit}`);
    return response.data;
  },

  // Get live metrics
  getLiveMetrics: async () => {
    const response = await api.get('/performance/metrics/live');
    return response.data;
  },

  // Reset metrics endpoint disabled for production safety
  // Performance metrics reset automatically on server restart
  // resetMetrics: async () => {
  //   const response = await api.post('/monitoring/performance/reset');
  //   return response.data;
  // },
};

// DEFAULT EXPORT
// ============================================================================

const apiServices = {
  authAPI,
  twoFactorAPI,
  templatesAPI,
  membershipAPI,
  memberPassAPI,
  multiTenantMembershipAPI,
  multiTenantCouponAPI,
  walletMembersAPI,
  couponsAPI,
  eventsAPI,
  analyticsAPI,
  usersAPI,
  companiesAPI,
  imagesAPI,
  performanceAPI,
  healthCheck,
  isAuthenticated,
  getStoredUserProfile,
  storeUserProfile,
  clearAuthData,
  handleAPIError,
};

export default apiServices;
